<script setup>
  import {computed, onMounted, provide, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import PatientinputLayout from "@components/Layout/PatientinputLayout.vue";
  import {Head, useForm, usePage} from "@inertiajs/vue3";
  import PatientinputComponentStepper from "@pages/Patientinput/Components/Stepper.vue";
  import PatientinputComponentStepBmp from "@pages/Patientinput/Components/Steps/StepBmp.vue";
  import PatientinputComponentStepCircumstances from "@pages/Patientinput/Components/Steps/StepCircumstances.vue";
  import PatientinputComponentStepCompliance from "@pages/Patientinput/Components/Steps/StepCompliance.vue";
  import PatientinputComponentStepDiagnoses from "@pages/Patientinput/Components/Steps/StepDiagnoses.vue";
  import PatientinputComponentStepEnd from "@pages/Patientinput/Components/Steps/StepEnd.vue";
  import PatientinputComponentStepLabordata from "@pages/Patientinput/Components/Steps/StepLabordata.vue";
  import PatientinputComponentStepMasterdata from "@pages/Patientinput/Components/Steps/StepMasterdata.vue";
  import PatientinputComponentStepMedication from "@pages/Patientinput/Components/Steps/StepMedication.vue";
  import PatientinputComponentStepMobility from "@pages/Patientinput/Components/Steps/StepMobility.vue";
  import PatientinputComponentStepSuccessCarer from "@pages/Patientinput/Components/Steps/StepSuccessCarer.vue";
  import PatientinputComponentStepSuccessPatient from "@pages/Patientinput/Components/Steps/StepSuccessPatient.vue";
  import PatientinputComponentStepSymptoms from "@pages/Patientinput/Components/Steps/StepSymptoms.vue";
  import PatientinputComponentStepWelcomeCarer from "@pages/Patientinput/Components/Steps/StepWelcomeCarer.vue";
  import PatientinputComponentStepWelcomePatient from "@pages/Patientinput/Components/Steps/StepWelcomePatient.vue";
  import PatientinputComponentStepWishesAims from "@pages/Patientinput/Components/Steps/StepWishesAims.vue";
  import {restartActivityTracking} from "@pharma4u/medicheck-activity-tracker";
  import {isBoolean} from "es-toolkit";
  import {isArray, isObject} from "es-toolkit/compat";
  import {CompactEncrypt, importSPKI} from "jose";

  defineOptions({
    name: "PagesPatientinputShow",
    layout: PatientinputLayout,
  });

  const props = defineProps({
    publicKey: {
      type: String,
      required: true,
    },
    saveUrl: {
      type: String,
      required: true,
    },
    isCarer: {
      type: Boolean,
      required: true,
    },
    isPatient: {
      type: Boolean,
      required: true,
    },
  });

  const form = useForm({
    meta: {
      is_carer: props.isCarer,
      is_patient: props.isPatient,
    },

    masterdata: {
      patient: {
        firstname: null,
        lastname: null,
        birthdate: null,
        gender: null,
        insurancenumber: null,
      },
      doc_name: null,
      nursing_home_carer: null,
      nursing_home_name: null,
      nursing_home_street: null,
      nursing_home_zip: null,
      nursing_home_city: null,
      nursing_home_ward: null,
      nursing_home_room: null,
    },

    medication: {
      pzns: [],
      list: [], // only internal for displaying
      bmp: [],
    },

    compliance: {
      confident: 3,
      forgotten: 3,
      affect: 3,
      pain: 0,
    },

    symptoms: {
      mivs: [],
      list: [], // only internal for displaying
    },

    circumstances: {
      mivs: [],
      attributes: {
        tubePatient: {
          selected: false,
          payload: {
            tube_position: null,
          },
        },
        insulinPump: false,
        port: false,
        palliativePatient: false,
        stoma: false,
        bitesMedicines: false,
        refusesMedications: false,
        swallowingDifficulties: false,

        // mobility
        stick: false,
        rollator: false,
        wheelchair: false,
        bedridden: false,
      },
    },

    laborvalues: {
      size: null,
      weight: null,
      bmi: null,
      bloodpressure: null,
      pulse: null,
    },

    wishes_aims: [],

    diagnoses: {
      mivs: [],
      list: [], // only internal for displaying
    },

    additional: null,
    contactdata: {
      name: null,
      telephone: null,
      email: null,
    },
  });

  const currentStep = ref(0);

  const error = ref(null);

  let encryptionKey = null;

  const steps = computed(() => {
    if (form.meta.is_patient) {
      return [
        {name: "Start", component: PatientinputComponentStepWelcomePatient},
        {name: "Medikationsplan", component: PatientinputComponentStepBmp},
        {name: "Stammdaten", component: PatientinputComponentStepMasterdata},
        {name: "Medikation", component: PatientinputComponentStepMedication},
        {name: "Compliance", component: PatientinputComponentStepCompliance},
        {name: "Probleme/Symptome", component: PatientinputComponentStepSymptoms},
        {name: "Lebensumstände", component: PatientinputComponentStepCircumstances},
        {name: "Labor-/Vitalwerte", component: PatientinputComponentStepLabordata},
        {name: "Wünsche und Ziele", component: PatientinputComponentStepWishesAims},
        {name: "Abschluss", component: PatientinputComponentStepEnd},
        {name: "Erfolg", component: PatientinputComponentStepSuccessPatient},
      ];
    }

    return [
      {name: "Start", component: PatientinputComponentStepWelcomeCarer},
      {name: "Medikationsplan", component: PatientinputComponentStepBmp},
      {name: "Stammdaten", component: PatientinputComponentStepMasterdata},
      {name: "Medikation", component: PatientinputComponentStepMedication},
      {name: "Labor-/Vitalwerte", component: PatientinputComponentStepLabordata},
      {name: "Diagnosen", component: PatientinputComponentStepDiagnoses},
      {name: "Probleme/Symptome", component: PatientinputComponentStepSymptoms},
      {name: "Mobilität", component: PatientinputComponentStepMobility},
      {name: "Weitere Angaben", component: PatientinputComponentStepCircumstances},
      {name: "Abschluss", component: PatientinputComponentStepEnd},
      {name: "Erfolg", component: PatientinputComponentStepSuccessCarer},
    ];
  });

  onMounted(() => {
    importSPKI(props.publicKey, "RSA-OAEP")
      .then((key) => {
        encryptionKey = key;
      })
      .catch(() => {
        error.value = true;
      });

    if (!import.meta.env.DEV) {
      // warn when reloading or closing tab
      window.addEventListener("beforeunload", (event) => {
        if (form.isDirty) {
          event.preventDefault();
          event.stopPropagation();
        }
      });
    }
  });

  const navigate = (toOrDirection) => {
    let simulatePageView = false;

    switch (toOrDirection) {
      case "+": {
        simulatePageView = true;
        currentStep.value++;
        break;
      }
      case "-": {
        currentStep.value--;
        break;
      }
      case "submit": {
        submit(() => {
          currentStep.value++;

          var _mtm = globalThis._mtm || [];
          _mtm.push({
            event: "customPageView",
            mcPageUrl: globalThis.location.pathname + "?step-" + (currentStep.value + 1),
            mcPageTitle: pageTitle.value,
          });
        });
        break;
      }
      default: {
        if (Number.isInteger(toOrDirection)) {
          currentStep.value = toOrDirection;
        }
      }
    }

    if (simulatePageView) {
      var _mtm = globalThis._mtm || [];
      _mtm.push({
        event: "customPageView",
        mcPageUrl: globalThis.location.pathname + "?step-" + (currentStep.value + 1),
        mcPageTitle: pageTitle.value,
      });
    }

    restartActivityTracking();
    window.scrollTo(0, 0);

    initStep();
  };

  const initStep = () => {
    // init step depending on active component name

    const activeStep = steps.value[currentStep.value];

    switch (activeStep.component.name) {
      case "PatientinputComponentStepEnd": {
        // initialize empty contactdata.name with patient name or
        // nursing_home_name, depending on is_patient
        form.contactdata.name = form.meta.is_patient
          ? (form.contactdata.name ?? form.masterdata.patient.firstname + " " + form.masterdata.patient.lastname)
          : (form.contactdata.name ?? form.masterdata.nursing_home_carer);
        break;
      }
    }
  };

  provide("navigate", navigate);

  const pageTitle = computed(() => {
    const step = steps.value[currentStep.value];
    return usePage().props.title + " - " + step.name;
  });
  const submit = async (callback) => {
    const formData = form.data();
    const meta = formData.meta;

    delete formData.meta; // does not need encryption
    delete formData.medication.list; // was only internal for displaying
    delete formData.symptoms.list; // was only internal for displaying
    delete formData.diagnoses.list; // was only internal for displaying

    if (meta.is_carer) {
      delete formData.compliance; // compliance is only for patient
    }

    const deleteEmptyData = (key) => {
      for (const index of Object.keys(formData[key])) {
        if (isArray(formData[key][index]) && formData[key][index].length === 0) {
          delete formData[key][index];
        } else if (isObject(formData[key][index]) && Object.keys(formData[key][index]).length === 0) {
          delete formData[key][index];
        } else if (isBoolean(formData[key][index]) && formData[key][index] === false) {
          delete formData[key][index];
        } else if (formData[key][index] === null) {
          delete formData[key][index];
        }
      }

      if (Object.keys(formData[key]).length === 0) {
        delete formData[key];
      }
    };

    for (const key of Object.keys(formData.circumstances.attributes)) {
      if (isBoolean(formData.circumstances.attributes[key]) && formData.circumstances.attributes[key] === false) {
        delete formData.circumstances.attributes[key];
      }
    }

    if (formData.circumstances.attributes.tubePatient.selected === false) {
      delete formData.circumstances.attributes.tubePatient;
    }

    deleteEmptyData("masterdata");
    deleteEmptyData("medication");
    deleteEmptyData("symptoms");
    deleteEmptyData("circumstances");
    deleteEmptyData("laborvalues");
    deleteEmptyData("diagnoses");

    if (formData.additional === null) {
      delete formData.additional;
    }

    if (formData.wishes_aims.length === 0) {
      delete formData.wishes_aims;
    }

    const encryptedData = await new CompactEncrypt(new TextEncoder().encode(JSON.stringify(formData)))
      .setProtectedHeader({alg: "RSA-OAEP", enc: "A256GCM"})
      .encrypt(encryptionKey);

    form
      .transform(() => ({
        meta: meta,
        encrypted: encryptedData,
        firstname_clipped: formData.masterdata.patient.firstname.slice(0, 3),
        lastname_clipped: formData.masterdata.patient.lastname.slice(0, 3),
        birthdate_clipped: new Date(formData.masterdata.patient.birthdate).getFullYear(),
        insurancenumber_clipped: formData.masterdata.patient.insurancenumber?.slice(0, 3),
      }))
      .post(props.saveUrl, {
        onSuccess: () => {
          if (typeof callback === "function") {
            callback();
          }
        },
      });
  };
</script>

<template>
  <Head :title="pageTitle" />

  <div class="flex h-full flex-col gap-5">
    <ComponentAlert v-if="error" class="mt-3" type="error" :test-id="getTestId()">
      <p>Beim Laden der Seite ist leider ein Fehler aufgetreten. Bitte versuchen Sie es erneut.</p>
    </ComponentAlert>

    <template v-else>
      <PatientinputComponentStepper
        v-if="currentStep > 1 && currentStep <= steps.length"
        :steps="steps.slice(2, -1)"
        :current-step="currentStep - 1"
        :disabled="currentStep === steps.length - 1"
        :test-id="getTestId()"
        @step-clicked="navigate"
      />

      <component :is="steps[currentStep].component" :form="form" :test-id="getTestId()" @navigate="navigate" />
    </template>
  </div>
</template>
